import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import type {
  IrrigationEstimateDto,
  IrrigationSiteDto,
} from '@lib/dto/irrigation.dto'
import {
  UNPRICEABLE_REASON_MESSAGES,
  type UnpriceableReason,
} from '@lib/helpers/irrigation-pricing'
import { irrigationService } from '@/services/IrrigationService'
import { irrigationKeys } from '@/hooks/queryKeys'

/** Changes whenever the server's copy of the site's repairs does. */
function repairsRevision(site: IrrigationSiteDto): string {
  return site.problems
    .map((problem) => `${problem.id}@${problem.updatedAt}`)
    .join(',')
}

/** "2 have no labor or parts recorded; 1 uses a part…" */
export function describeUnpriceable(reasons: readonly UnpriceableReason[]): string {
  const counts = new Map<UnpriceableReason, number>()
  for (const reason of reasons) {
    counts.set(reason, (counts.get(reason) ?? 0) + 1)
  }
  return [...counts]
    .map(([reason, count]) => {
      const message = UNPRICEABLE_REASON_MESSAGES[reason]
      return count === 1
        ? `1 ${message}`
        : `${String(count)} ${message.replace(/^has /, 'have ').replace(/^uses /, 'use ')}`
    })
    .join('; ')
}

/**
 * What a site's open repairs come to, priced by the server from the copy of
 * Housecall Pro. Shared by the line under the map and the bid tray, so the
 * two read one request. Refetches when a sync changes the repairs.
 */
export function useRepairEstimate(
  site: IrrigationSiteDto,
  /** False until the visit it prices has reached the server. */
  enabled: boolean,
): UseQueryResult<IrrigationEstimateDto> {
  return useQuery({
    enabled,
    queryKey: irrigationKeys.estimate(
      site.siteId,
      `${site.visitId ?? 'current'}:${repairsRevision(site)}`,
    ),
    queryFn: () => irrigationService.estimate(site.siteId, site.visitId),
    retry: false,
    // Kept while the next revision loads, so the tray does not blank out
    // between a sync and its new price.
    placeholderData: (previous) => previous,
  })
}

/**
 * Repairs that cannot be priced, under the survey map.
 *
 * A repair that cannot be priced stops a proposal being sent, and the
 * cheapest moment to fix one is while the technician is still standing over
 * it. What the priced ones come to is in the bid tray in the header, with the
 * rest of the bid. Nothing is shown when everything prices, or offline.
 */
export function RepairEstimate({
  site,
  enabled = true,
}: {
  site: IrrigationSiteDto
  /** False until the visit it prices has reached the server. */
  enabled?: boolean
}) {
  const estimate = useRepairEstimate(site, enabled)
  const data = estimate.data
  if (data === undefined || data.unpriceable.length === 0) return null

  return (
    <p className="text-xs text-warning">
      Cannot be priced yet:{' '}
      {describeUnpriceable(data.unpriceable.map((entry) => entry.reason))}.
    </p>
  )
}
